import React from "react";
import { motion } from "framer-motion";
import SolutionsTabs from "../components/SolutionsTabs";
import Carousel from "../components/Carousel";
import Card from "../components/Card";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import TrustedBy from "../components/TrustedBy";
import SolutionsSection from "../components/SolutionsSection";
import SEO from "../components/SEO";
import StructuredData, {
  createOrganizationSchema,
  createWebSiteSchema,
  createSoftwareApplicationSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const Home = () => {
  const testimonials = [
    {
      quote:
        "It's an all-in-one, online management tool that makes project management easy. I can follow up the economy, time, quality, tasks, risks, etc. of all of my projects. I can do all of this online, which makes me very mobile. It lets me set up my workflows and projects as I please, and keep all my files on the platform as well. Estabild doesn't just make remote work possible, it makes it as effortless as sitting in the office.",
      author: "Sven-Åke Yashar Khezri Kraim",
      position: "Project Developer",
      company: "NREP",
      avatar: "/home/testimonials/6552731dfe00305f497ce941_sven-ake nrep.jpg",
      companyLogo:
        "/home/company-logos/64bfe4824f66a77c8c788fe1_nrep logo dark.png",
    },
    {
      quote:
        "We are saving around 20 hours per week by using Estabild. Estabild is helping us track our project's expenses & incomes accurately. This has allowed us to do a financial cross-checking assessment in only one step instead of three steps.",
      author: "Fabian Moscoso",
      position: "Real Estate General Manager",
      company: "Mutualista Azuay",
      avatar: "/home/testimonials/64bfe4824f66a77c8c788fc5_Fabian Moscoso.jpeg",
      companyLogo:
        "/home/company-logos/64bfe4824f66a77c8c788fe3_mutualista logo dark.png",
    },
    {
      quote:
        "Estabild is a smart solution for project developers that is helping our management team to have a global overview of the costs, plans and main risks related to our construction house projects. This tool has allowed our leadership team to have good visibility of progress and control costs for each project.",
      author: "Finnur Gudmundsson",
      position: "Partner",
      company: "Tekta",
      avatar:
        "/home/testimonials/6552731dfe00305f497ce92a_Finnur Gudmundsson.png",
      companyLogo:
        "/home/company-logos/64bfe4824f66a77c8c788fdd_tekta logo dark.png",
    },
    {
      quote:
        "We have worked in the design and furnishings business for a long time, and Costa Jama is one of our first development projects. My greatest concern has been on how to manage and gain control during the project execution phase. Estabild has been a great tool to help us stay on track.",
      author: "Eduardo Perez D.",
      position: "President & CEO",
      company: "AH Corp",
      avatar:
        "/home/testimonials/6552731dfe00305f497ce92e_Eduardo Perez D..png",
      companyLogo:
        "/home/company-logos/6552731dfe00305f497ce93a_costa jama logo dark.png",
    },
    {
      quote:
        "Estabild is helping our company to organize and streamline critical project information among key stakeholders. Now, our teammates can easily collaborate and share status updates. Estabild gives upper management a clear overview that enables us to make better decisions about our portfolio performance.",
      author: "Jaime Macias",
      position: "Head of Project Management",
      company: "Ciport & Tecnac",
      avatar: "/home/testimonials/6552731dfe00305f497ce932_jaime macias.jpg",
      companyLogo:
        "/home/company-logos/6552731dfe00305f497ce93d_cipte logo dark.png",
    },
    {
      quote:
        "I use Estabild to manage a range of development projects and it is becoming a valuable tool in the delivery of projects within my construction company. The program is intuitive and quickly aggregates data into simple and easy dashboard with customisable report templates, making the business more efficient in reporting to our investors.",
      author: "Nick Levett",
      position: "Managing Director",
      company: "Elements Real Estate Group",
      avatar: "/home/testimonials/6552731dfe00305f497ce92c_Nick Levett.png",
      companyLogo:
        "/home/company-logos/6552731dfe00305f497ce936_elements logo dark.png",
    },
    {
      quote:
        "Estabild has changed the way we engage with projects. It brought unprecedented traceability and accountability to the project performance. Our leadership team was able to make timely decisions that helped us avoid major cost overruns.",
      author: "Max Tattenbach",
      position: "COO",
      company: "Martec",
      avatar: "/home/testimonials/6552731dfe00305f497ce924_Max Tattenbach.jpeg",
      companyLogo:
        "/home/company-logos/6552731dfe00305f497ce93c_martec logo dark.svg",
    },
    {
      quote:
        "Estabild has helped us gain an organized control in our development project. Our stakeholders have the visibility and awareness they need regarding what's happening on site in real-time. This is invaluable to us.",
      author: "Alexandra Iturrale",
      position: "Executive assistant",
      company: "AHC SCC Investments",
      avatar:
        "/home/testimonials/64bfe4824f66a77c8c788f60_Alexandra Iturrale.png",
      companyLogo:
        "/home/company-logos/6552731dfe00305f497ce93a_costa jama logo dark.png",
    },
  ];

  const homeSeo = seoData.home;
  const structuredData = [
    createOrganizationSchema(),
    createWebSiteSchema(),
    createSoftwareApplicationSchema(),
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={homeSeo.title}
        description={homeSeo.description}
        keywords={homeSeo.keywords}
        url={homeSeo.url}
      />
      <StructuredData data={structuredData} />
      
      {/* Hero Section */}
      <Hero />

      <TrustedBy />
      <SolutionsTabs />
      <SolutionsSection />

      {/* Integration Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportSettings}
          transition={{ duration: 0.6 }}
          className="container-custom"
        >
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
              Seamless integration with your tools
            </h2>
            <p className="text-sm sm:text-base lg:text-md text-gray-300 max-w-4xl mx-auto px-4">
              No need for big changes in your workflow nor steep learning curves
              for your team. <br className="hidden sm:block" />
              We will ensure a smooth transition to Estabild and support you
              along the way.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 sm:gap-6 items-center justify-items-center">
            {[
              { name: "Fortnox", img: "/integrations/fortnox.png" },
              { name: "MS Project", img: "/integrations/ms-project.png" },
              { name: "Oracle", img: "/integrations/oracle.png" },
              { name: "Excel", img: "/integrations/excel.png" },
              { name: "Visma", img: "/integrations/visma.png" },
              { name: "Outlook", img: "/integrations/outlook.png" },
              { name: "QuickBooks", img: "/integrations/quickbooks.png" },
              { name: "Google Drive", img: "/integrations/gdrive.png" },
              { name: "SharePoint", img: "/integrations/sharepoint.png" },
              { name: "SAP", img: "/integrations/sap.png" },
              { name: "PDF", img: "/integrations/pdf.png" },
              {
                name: "Internal Tool",
                img: "/integrations/internal-tool.png",
              },
              { name: "Sage", img: "/integrations/sage.png" },
              { name: "Gmail", img: "/integrations/gmail.png" },
            ].map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={viewportSettings}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-gray-800/80 backdrop-blur-sm border border-gray-700/50 text-white rounded-xl p-3 sm:p-4 hover:scale-110 hover:bg-gray-700/80 hover:border-gray-600/50 hover:shadow-lg transition-all duration-300 w-full flex items-center justify-center"
              >
                <img
                  src={tool.img}
                  alt={tool.name}
                  className="h-12 sm:h-14 lg:h-16 w-auto max-w-full object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Customer Value Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
            className="section-heading text-center mx-auto mb-8"
          >
            Value offering that delights our customers
          </motion.h2>

          {/* Customer Stories Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Mutualista Story */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6 }}
            >
              <Card className="h-full overflow-hidden">
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-6">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/LJFRSxzPnTc"
                    title="Estabild customer Mutualista Azuay"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
                <div className="flex flex-col justify-center">
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-black mb-2">
                      Saved 20 hours/week
                    </div>
                    <p className="text-gray-600 text-lg">
                      on project tracking and reporting
                    </p>
                  </div>
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-black mb-2">
                      Increased 80%
                    </div>
                    <p className="text-gray-600 text-lg">
                      in data accessibility among stakeholders
                    </p>
                  </div>
                  <a
                    href="https://youtube.com/watch?v=LJFRSxzPnTc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black font-semibold hover:text-gray-700 transition-colors inline-flex items-center gap-2 uppercase text-md"
                  >
                    Watch Story
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 7H13M13 7L7 1M13 7L7 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* 15 Hours Saved Story */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportSettings}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="h-full">
                <img
                  src="/home/customer-stories/6557385fbd728367be004de8_img.png"
                  alt="Customer story"
                  className="w-full h-auto aspect-video object-cover rounded-lg mb-6"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="flex flex-col justify-center">
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-black mb-2">
                      Saved 15 hours/week
                    </div>
                    <p className="text-gray-600 text-lg">
                      on administrative tasks
                    </p>
                  </div>
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-black mb-2">
                      Reduced 40%
                    </div>
                    <p className="text-gray-600 text-lg">
                      of resources needed to produce the required deliverables
                    </p>
                  </div>
                  <a
                    href="https://estabild.wistia.com/medias/ciusajorig"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black font-semibold hover:text-gray-700 transition-colors inline-flex items-center gap-2 uppercase text-md"
                  >
                    Watch Webinar
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 7H13M13 7L7 1M13 7L7 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
            className="section-heading text-center mx-auto mb-8"
          >
            What our customers say
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-6xl mx-auto"
          >
            <Carousel
              items={testimonials.map((testimonial, index) => (
                <div key={index} className="px-2 sm:px-4">
                  <Card noShadow className="h-full p-6 sm:p-10 lg:p-20">
                    <p className="text-gray-700 text-sm sm:text-base lg:text-xl mb-6 sm:mb-8 leading-relaxed">
                      {testimonial.quote}
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.author}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <div>
                          <div className="font-semibold text-gray-900 text-sm sm:text-base">
                            {testimonial.author}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600">
                            {testimonial.position} at {testimonial.company}
                          </div>
                        </div>
                      </div>
                      <img
                        src={testimonial.companyLogo}
                        alt={testimonial.company}
                        className="h-8 sm:h-10 w-24 sm:w-32 object-contain flex-shrink-0 self-end sm:self-auto"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  </Card>
                </div>
              ))}
              autoPlay={true}
              interval={8000}
            />
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "/home/metrics/6565b1bc2e3a65322e624516_time.svg",
                text: "Over 60% saved in time on operational tasks",
              },
              {
                icon: "/home/metrics/6565b1bca850dda1e27da39e_analytics.svg",
                text: "Up to 50% increase in productivity efficiency",
              },
              {
                icon: "/home/metrics/6565b1bca0eee3ef6f9b372e_chart.svg",
                text: "Over 3x increase in data accessibility",
              },
              {
                icon: "/home/metrics/6565b1bc31fb7411bb0da374_people-safe.svg",
                text: "Reduction and mitigation of project risks",
              },
            ].map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-2xl hover:bg-gray-50 transition-all duration-300 hover:scale-105"
              >
                <div className="w-16 h-16 mb-4 flex items-center justify-center rounded-xl bg-primary-50 p-3">
                  <img
                    src={metric.icon}
                    alt=""
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <p className="text-lg font-semibold text-gray-900 leading-relaxed">
                  {metric.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-20 sm:pt-24 lg:pt-32 pb-24 sm:pb-32 lg:pb-40 bg-gradient-to-br from-primary-600 via-primary-600 to-primary-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800/20 via-transparent to-primary-800/20"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-heading font-bold mb-6 sm:mb-8 leading-tight tracking-tight drop-shadow-lg">
              Focus on your projects growth.
              <br />
              We'll take care of the reporting.
            </h2>
            <Link
              to="/contact"
              className="mt-6 sm:mt-8 lg:mt-10 text-2xl sm:text-3xl lg:text-5xl font-heading font-bold text-white hover:text-primary-100 transition-all duration-300 group inline-flex items-center gap-4 sm:gap-6 hover:scale-105 active:scale-100"
            >
              Book demo
              <img
                src="/home/6557385fbd728367be004dcf_Vectors-Wrapper.svg"
                alt=""
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 group-hover:translate-x-3 transition-transform duration-300"
              />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
